import { useEffect } from 'react';
import {
  ApiBlueprint,
  AppRootElementBlueprint,
  createApiFactory,
  createApiRef,
  createFrontendModule,
  createFrontendPlugin,
  configApiRef,
  discoveryApiRef,
  FrontendModule,
  FrontendPlugin,
  oauthRequestApiRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  analyticsApiRef,
  ApiRef,
  BackstageIdentityApi,
  featureFlagsApiRef,
  identityApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  storageApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  ScmAuth,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { GoogleAnalytics4 } from '@backstage-community/plugin-analytics-module-ga4';
import { MARKETPLACE_ROUTES } from '@ciandtspike.io/plugin-ciandtspike-common';
import { resourceDetailTabsApiRef } from '@ciandtspike.io/plugin-ciandtspike-marketplace';
import { cloudRunDetailTab } from '@ciandtspike.io/plugin-resource-detail-cloudrun';
import { repositoryDetailTab } from '@ciandtspike.io/plugin-resource-detail-repository';
import { costsDetailTab } from '@ciandtspike.io/plugin-ciandtspike-costs';
import { OAuth2 } from '@backstage/core-app-api';
import { Navigate } from 'react-router-dom';
import { NewOnboardingPage } from '@internal/backstage-plugin-ciandtspike-onboarding-react';
import {
  compatWrapper,
  convertLegacyRouteRef,
} from '@backstage/core-compat-api';
import {
  InfraWalletPage,
  infraWalletPlugin,
} from '@electrolux-oss/plugin-infrawallet';
import {
  ReactiveLocalStorageFeatureFlags,
  useRolloutFlag,
  PERMISSIONS_TAB_FLAG,
} from './featureFlags';
import {
  PluginAccessPage,
  useCanViewPlugin,
} from '@internal/plugin-rebac-explorer-frontend';
import {
  AppRootWrapperBlueprint,
  NavContentBlueprint,
  SignInPageBlueprint,
  ThemeBlueprint,
  TranslationBlueprint,
} from '@backstage/plugin-app-react';
import { PermissionsTabBridge } from './components/PermissionsTabBridge/PermissionsTabBridge';
import { SidebarNav } from './components/Root/SidebarNav';
import { PendoBootstrap } from './lib/pendo/PendoBootstrap';
import { spikeSearchModalTranslations } from './components/search/SpikeSearchModal';
import {
  SpikeOAuthRequestDialog,
  spikeOAuthRequestDialogTranslations,
} from './components/auth/SpikeOAuthRequestDialog';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { infrawalletViewPermission } from './permissions/infrawallet-permissions';
import {
  FormFieldBlueprint,
  createFormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import SchoolIcon from '@material-ui/icons/School';
import { UnifiedThemeProvider } from '@backstage/theme';
import {
  ciandtSpikeDarkTheme,
  ciandtSpikeTheme,
} from './themes/ciandtSpikeTheme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness3';

// Keycloak OIDC auth APIs — específicas do host, sem equivalente upstream.
// Consumidas pela SpikeSignInPage (SignInPageBlueprint) registrada abaixo.
export const keycloakOIDCAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({ id: 'auth.keycloak' });

// Provider "oidc-dev": mesmas credenciais Keycloak, sem kc_idp_hint.
// Exibe a tela nativa do Keycloak — permite login de usuários manuais (dev only).
export const keycloakNativeAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({ id: 'auth.keycloak-native' });

const keycloakApi = ApiBlueprint.make({
  name: 'keycloak-oidc',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: keycloakOIDCAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
          OAuth2.create({
            configApi,
            discoveryApi,
            oauthRequestApi,
            provider: {
              id: 'oidc',
              title: 'CI&T Spike',
              icon: () => null,
            },
            environment: configApi.getOptionalString('auth.environment'),
            defaultScopes: ['openid', 'profile', 'email', 'roles'],
            popupOptions: { size: { fullscreen: true } },
          }),
      }),
    ),
});

const keycloakNativeApi = ApiBlueprint.make({
  name: 'keycloak-native',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: keycloakNativeAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
          OAuth2.create({
            configApi,
            discoveryApi,
            oauthRequestApi,
            provider: {
              id: 'oidc-dev',
              title: 'CI&T Spike (dev)',
              icon: () => null,
            },
            environment: configApi.getOptionalString('auth.environment'),
            defaultScopes: ['openid', 'profile', 'email', 'roles'],
            popupOptions: { size: { fullscreen: true } },
          }),
      }),
    ),
});

// Substitui o default LocalStorageFeatureFlags por ReactiveLocalStorageFeatureFlags,
// que emite um evento DOM na mesma aba ao salvar (browsers não disparam `storage`
// para cross-same-tab writes).
const featureFlagsApi = ApiBlueprint.make({
  name: 'feature-flags',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: featureFlagsApiRef,
        deps: {},
        factory: () => new ReactiveLocalStorageFeatureFlags(),
      }),
    ),
});

// APIs migradas do antigo array `apis` (compat bridge convertLegacyAppOptions).
const customHashingFunction = async (
  userEntityRef: string,
): Promise<string> => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(userEntityRef);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    return btoa(userEntityRef);
  }
};

const scmIntegrationsApi = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: scmIntegrationsApiRef,
        deps: { configApi: configApiRef },
        factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
      }),
    ),
});

const analyticsApi = ApiBlueprint.make({
  name: 'analytics-ga4',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: analyticsApiRef,
        deps: { configApi: configApiRef, identityApi: identityApiRef },
        factory: ({ configApi, identityApi }) =>
          GoogleAnalytics4.fromConfig(configApi, {
            identityApi,
            userIdTransform: customHashingFunction,
          }),
      }),
    ),
});

const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams => defineParams(ScmAuth.createDefaultApiFactory()),
});

// name/id `light` e `dark` são idênticos aos temas default do plugin `app`
// (`theme:app/light` e `theme:app/dark`): vindo de um módulo do `app`, estes
// sobrescrevem os defaults do Backstage in-place, então os seletores
// Light/Dark/Auto passam a renderizar a identidade visual CI&T Spike.
const lightTheme = ThemeBlueprint.make({
  name: 'light',
  params: {
    theme: {
      id: 'light',
      title: 'CI&T Spike Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={ciandtSpikeTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

const darkTheme = ThemeBlueprint.make({
  name: 'dark',
  params: {
    theme: {
      id: 'dark',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={ciandtSpikeDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

const sidebarNav = NavContentBlueprint.make({
  name: 'ciandtspike',
  params: { component: SidebarNav },
});

const spikeSearchModalTranslationsExtension = TranslationBlueprint.make({
  name: 'spike-search-modal',
  params: { resource: spikeSearchModalTranslations },
});

const spikeOAuthRequestDialogTranslationsExtension = TranslationBlueprint.make(
  {
    name: 'spike-oauth-request-dialog',
    params: { resource: spikeOAuthRequestDialogTranslations },
  },
);

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      const { SignInPageSelector } = await import(
        './components/auth/SignInPageSelector'
      );
      return SignInPageSelector;
    },
  },
});

// FLWP-63804 — religa o PermissionsTabProvider, perdido na migração para o novo
// Frontend System (1baa3af1), mesmo padrão do ResourceDetailTabsInitializer acima.
const permissionsTabWrapper = AppRootWrapperBlueprint.make({
  name: 'permissions-tab-bridge',
  params: { component: PermissionsTabBridge },
});

// Estas extensions pertencem ao plugin `app`, não ao `app-host`, porque atuam
// sobre APIs/inputs cujo dono é o `app` e só aceitam extensions do próprio `app`:
// - feature flags / scm-integrations / analytics: estas APIs têm o `app` como
//   dono (defaults do app-defaults); o override só vale vindo do próprio `app`;
// - themes: ThemeBlueprint anexa ao input interno `themes` de `api:app/app-theme`;
// - sidebar nav: NavContentBlueprint anexa ao input interno `content` de `app/nav`;
// - sign-in page: SignInPageBlueprint só é aceito vindo do plugin `app`.
// Por isso são entregues via módulo do `app`.
export const appModuleHostCustomizations: FrontendModule = createFrontendModule(
  {
    pluginId: 'app',
    extensions: [
      featureFlagsApi,
      scmIntegrationsApi,
      analyticsApi,
      scmAuthApi,
      lightTheme,
      darkTheme,
      sidebarNav,
      signInPage,
      permissionsTabWrapper,
      spikeSearchModalTranslationsExtension,
      spikeOAuthRequestDialogTranslationsExtension,
    ],
  },
);

// Marca onboarding como concluído ao visitar a página (usado pelo HomeRedirect
// para decidir para onde redirecionar em `/`).
const OnboardingPageWithCompletion = () => {
  const storageApi = useApi(storageApiRef);
  useEffect(() => {
    storageApi.forBucket('spike').set('navigation.onboarding-completed', true);
  }, [storageApi]);
  return <NewOnboardingPage />;
};

const onboardingPage = PageBlueprint.make({
  name: 'onboarding',
  params: {
    path: '/onboarding',
    title: 'Onboarding',
    icon: <SchoolIcon fontSize="inherit" />,
    loader: async () => <OnboardingPageWithCompletion />,
  },
});

// Redireciona `/` para `/marketplace` (se onboarding concluído) ou `/onboarding`.
const HomeRedirect = () => {
  const storageApi = useApi(storageApiRef);
  const snap = storageApi
    .forBucket('spike')
    .snapshot<boolean>('navigation.onboarding-completed');
  const onboardingCompleted =
    snap.presence === 'present' && snap.value === true;
  return (
    <Navigate
      to={onboardingCompleted ? MARKETPLACE_ROUTES.root : '/onboarding'}
      replace
    />
  );
};

// Página inicial em `/`. Não registramos `@backstage/plugin-home/alpha` porque a
// page dele nasce como `page:home` em `/home`; aqui usamos uma page própria (id
// distinto) para manter a Home customizada em `/`.
const homePage = PageBlueprint.make({
  name: 'home',
  params: {
    path: '/',
    loader: async () => <HomeRedirect />,
  },
});

// InfraWallet não tem `/alpha`; a página `/costs` permanece registrada à mão.
// InfraWalletPage é uma routable extension legada: precisa do routeRef dela
// (infraWalletPlugin.routes.root) associado à page para ser descoberta na árvore.
const costsPage = PageBlueprint.make({
  name: 'costs',
  params: {
    path: '/costs',
    routeRef: convertLegacyRouteRef(infraWalletPlugin.routes.root),
    loader: async () =>
      compatWrapper(
        <RequirePermission permission={infrawalletViewPermission}>
          <InfraWalletPage />
        </RequirePermission>,
      ),
  },
});

// Guard para /access: aguarda canView (undefined = carregando) antes de redirecionar.
// Evita redirect prematuro enquanto a chamada ao rebac API ainda está em voo.
const PluginAccessPageGuard = () => {
  const flagEnabled = useRolloutFlag(PERMISSIONS_TAB_FLAG);
  const canView = useCanViewPlugin('access-management');
  if (!flagEnabled || canView === false) return <Navigate to="/" replace />;
  if (canView === undefined) return null;
  return <PluginAccessPage />;
};

// rebac-explorer-frontend não tem /alpha; page registrada manualmente.
const accessPage = PageBlueprint.make({
  name: 'access',
  params: {
    path: '/access',
    loader: async () => <PluginAccessPageGuard />,
  },
});

const customDynamicEntityPickerSchema = makeFieldSchema({
  uiOptions: zImpl =>
    zImpl.object({
      catalogFilter: zImpl
        .array(zImpl.record(zImpl.string(), zImpl.string()))
        .optional(),
    }),
  output: zImpl =>
    zImpl.object({
      entityName: zImpl.string(),
      entityNamespace: zImpl.string(),
    }),
});

// CustomDynamicEntityPicker migrado de createScaffolderFieldExtension (old)
// para FormFieldBlueprint + createFormField (new scaffolder system).
const customDynamicEntityPickerField = FormFieldBlueprint.make({
  name: 'custom-dynamic-entity-picker',
  params: {
    field: async () => {
      const { CustomDynamicEntityPicker } = await import(
        './scaffolder/CustomDynamicEntityPicker/CustomDynamicEntityPickerExtension'
      );
      return createFormField({
        name: 'CustomDynamicEntityPicker',
        component: CustomDynamicEntityPicker,
        schema: customDynamicEntityPickerSchema,
      });
    },
  },
});

// Inicializa a sidebar fechada (não pinned) na primeira visita, preservando a
// preferência do usuário em visitas seguintes. Antes vivia no root JSX do compat
// bridge; agora é um app root element do sistema novo.
const SidebarPinInitializer = () => {
  useEffect(() => {
    if (localStorage.getItem('sidebarPinState') === null) {
      localStorage.setItem('sidebarPinState', 'false');
    }
  }, []);
  return null;
};

const sidebarPinInitializerElement = AppRootElementBlueprint.make({
  name: 'sidebar-pin-initializer',
  params: { element: <SidebarPinInitializer /> },
});

// Inicializa o Pendo (product analytics) após o login. Gateado por
// app.analytics.pendo.enabled no app-config — sem a config, não carrega o SDK.
const pendoBootstrapElement = AppRootElementBlueprint.make({
  name: 'pendo-bootstrap',
  params: { element: <PendoBootstrap /> },
});

// Substitui o OAuthRequestDialog padrão do @backstage/plugin-app (desabilitado
// via app.extensions no app-config.yaml) por um popup com o copy e o visual do
// Spike — o original citava "Keycloak" e usava o Dialog MUI genérico, fora do
// padrão da SpikeSignInPage.
const spikeOAuthRequestDialogElement = AppRootElementBlueprint.make({
  name: 'spike-oauth-request-dialog',
  params: { element: <SpikeOAuthRequestDialog /> },
});

// FLWP-69422 — o registro das abas "Cloud Run" e "Repositório" na página de
// detalhe do marketplace (ServiceDetailsPage) vivia em packages/app/src/apis.ts,
// removido na migração para o novo Frontend System. resourceDetailTabsApiRef é
// dono do plugin marketplace, então não pode ser sobrescrito via ApiBlueprint
// (só o dono/app têm esse privilégio) — o registro é feito como efeito de um
// app root element, igual ao SidebarPinInitializer acima.
const ResourceDetailTabsInitializer = () => {
  const tabsApi = useApi(resourceDetailTabsApiRef);
  useEffect(() => {
    tabsApi.register(cloudRunDetailTab);
    tabsApi.register(repositoryDetailTab);
    tabsApi.register(costsDetailTab);
  }, [tabsApi]);
  return null;
};

const resourceDetailTabsInitializerElement = AppRootElementBlueprint.make({
  name: 'resource-detail-tabs-initializer',
  params: { element: <ResourceDetailTabsInitializer /> },
});

export const hostPlugin: FrontendPlugin = createFrontendPlugin({
  pluginId: 'app-host',
  // Sem title, o PluginHeader do novo UI exibiria o id cru "app-host" nas pages
  // do host (Home, Onboarding, Costs, My Resources).
  title: 'CI&T Spike',
  featureFlags: [{ name: 'ft-enable-customizable-home-page' }],
  extensions: [
    keycloakApi,
    keycloakNativeApi,
    onboardingPage,
    homePage,
    costsPage,
    accessPage,
    customDynamicEntityPickerField,
    sidebarPinInitializerElement,
    pendoBootstrapElement,
    spikeOAuthRequestDialogElement,
    resourceDetailTabsInitializerElement,
  ],
});
